import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'

const BACKEND = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'
const TARGET_PER_PERSON = 120

export default function HomePage() {
    const [stats, setStats] = useState(null)

    useEffect(() => {
        const fetchStats = () => {
            fetch(`${BACKEND}/dataset/stats`)
                .then(r => r.ok ? r.json() : null)
                .then(data => data && setStats(data))
                .catch(() => {})
        }
        fetchStats()
        const id = setInterval(fetchStats, 30000)
        return () => clearInterval(id)
    }, [])

    return (
        <div className="min-vh-100 d-flex flex-column" style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1b2d45 60%, #0d6efd22 100%)' }}>
            {/* Navbar */}
            <nav className="navbar px-4 py-3" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="navbar-brand text-white fw-bold fs-5 d-flex align-items-center gap-2">
                    <span style={{ fontSize: '1.4rem' }}>🎭</span>
                    Awesome Faces
                </span>
                <span className="badge bg-primary">Dataset v1.0</span>
            </nav>

            {/* Hero */}
            <main className="flex-grow-1 d-flex align-items-center justify-content-center px-3 py-5">
                <div className="text-center" style={{ maxWidth: 740 }}>
                    {/* Badge */}
                    <div className="mb-4">
                        <span className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(13,110,253,0.2)', color: '#6ea8fe', border: '1px solid rgba(13,110,253,0.4)', fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                            MASTER DATA SCIENCE &mdash; PROJET DE SOUTENANCE
                        </span>
                    </div>

                    {/* Title */}
                    <h1 className="display-4 fw-bold text-white mb-3" style={{ lineHeight: 1.15 }}>
                        Collecte de données faciales
                        <br />
                        <span style={{ color: '#6ea8fe' }}>pour la reconnaissance faciale avec occlusion</span>
                    </h1>

                    {/* Subtitle */}
                    <p className="lead mb-5" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.1rem' }}>
                        Cette plateforme permet de constituer un dataset annoté d'images faciales —
                        avec et sans masque — destiné à l'entraînement d'un modèle de reconnaissance faciale avec occlusion
                        dans le cadre d'un projet de recherche en vision par ordinateur.
                    </p>

                    {/* CTA */}
                    <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-5">
                        <Link to="/start" className="btn btn-primary btn-lg px-5 py-3 fw-semibold shadow" style={{ borderRadius: 12, fontSize: '1rem' }}>
                            Participer à la collecte
                            <span className="ms-2">→</span>
                        </Link>
                        <a href="#how-it-works" className="btn btn-lg px-5 py-3 fw-semibold" style={{ borderRadius: 12, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)', fontSize: '1rem' }}>
                            Comment ça marche ?
                        </a>
                    </div>

                    {/* Stats pills */}
                    <div className="d-flex flex-wrap gap-3 justify-content-center">
                        {[
                            { icon: '📸', label: '120 photos / personne', sub: '60 sans masque · 60 avec masque' },
                            { icon: '🔒', label: 'Données confidentielles', sub: 'Usage strictement académique' },
                            { icon: '⚡', label: 'IA embarquée', sub: 'Inférence 100% dans le navigateur' },
                        ].map(({ icon, label, sub }) => (
                            <div key={label} className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <span style={{ fontSize: '1.2rem' }}>{icon}</span>
                                <div className="text-start">
                                    <div className="text-white fw-semibold" style={{ fontSize: '0.85rem' }}>{label}</div>
                                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem' }}>{sub}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            {/* ── Dataset Statistics ───────────────────────────────────── */}
            {stats && (
                <section id="stats" className="py-5 px-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="container" style={{ maxWidth: 900 }}>
                        <div className="text-center mb-4">
                            <h2 className="text-white fw-bold mb-1" style={{ fontSize: '1.5rem' }}>État du dataset</h2>
                            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem' }}>
                                Données en temps réel — mises à jour à chaque participation
                            </p>
                        </div>

                        {/* KPI cards */}
                        <div className="row g-3 mb-4">
                            {[
                                {
                                    value: stats.totalPersons,
                                    label: 'Participants',
                                    sub: 'Objectif : 100',
                                    //sub: `dont ${stats.personsComplete} profil${stats.personsComplete > 1 ? 's' : ''} complet${stats.personsComplete > 1 ? 's' : ''}`,
                                    color: '#6ea8fe',
                                },
                                {
                                    value: stats.totalImages.toLocaleString('fr-FR'),
                                    label: 'Images collectées',
                                    sub: 'Objectif : 12000',
                                    //sub: `objectif : ${(stats.totalPersons * TARGET_PER_PERSON).toLocaleString('fr-FR')}`,
                                    color: '#6ea8fe',
                                },
                                {
                                    value: stats.breakdown.no_mask.toLocaleString('fr-FR'),
                                    label: 'Sans masque',
                                    sub: 'Objectif : 6000',
                                    //sub: `${stats.totalImages > 0 ? ((stats.breakdown.no_mask / stats.totalImages) * 100).toFixed(1) : 0} % du total`,
                                    color: '#75b798',
                                },
                                {
                                    value: stats.breakdown.mask.toLocaleString('fr-FR'),
                                    label: 'Avec masque',
                                    sub: 'Objectif : 6000',
                                    //sub: `${stats.totalImages > 0 ? ((stats.breakdown.mask / stats.totalImages) * 100).toFixed(1) : 0} % du total`,
                                    color: '#ea868f',
                                },
                            ].map(({ value, label, sub, color }) => (
                                <div key={label} className="col-6 col-md-3">
                                    <div className="p-3 rounded-3 text-center h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                        <div className="fw-bold mb-1" style={{ fontSize: '2rem', color, lineHeight: 1.1 }}>{value}</div>
                                        <div className="text-white fw-semibold" style={{ fontSize: '0.82rem' }}>{label}</div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', marginTop: 2 }}>{sub}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Progress bars */}
                        <div className="p-4 rounded-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>

                            {/* Taux de remplissage global */}
                            {(() => {
                                const capacity = 12000
                                const pct = capacity > 0 ? Math.min((stats.totalImages / capacity) * 100, 100) : 0
                                return (
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-white fw-semibold" style={{ fontSize: '0.85rem' }}>Taux de remplissage global</span>
                                            <span style={{ color: '#6ea8fe', fontSize: '0.82rem', fontWeight: 600 }}>{pct.toFixed(1)} %</span>
                                        </div>
                                        <div className="rounded-pill overflow-hidden" style={{ height: 10, background: 'rgba(255,255,255,0.08)' }}>
                                            <div className="h-100 rounded-pill" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #0d6efd, #6ea8fe)', transition: 'width 0.8s ease' }} />
                                        </div>
                                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem', marginTop: 4 }}>
                                            {stats.totalImages.toLocaleString('fr-FR')} / {capacity.toLocaleString('fr-FR')} images (cible : {TARGET_PER_PERSON * 100})
                                        </div>
                                    </div>
                                )
                            })()}

                            {/* Profils complets */}
                            {/* {(() => {
                                const pct = stats.totalPersons > 0 ? (stats.personsComplete / stats.totalPersons) * 100 : 0
                                return (
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-white fw-semibold" style={{ fontSize: '0.85rem' }}>Profils complets</span>
                                            <span style={{ color: '#75b798', fontSize: '0.82rem', fontWeight: 600 }}>{stats.personsComplete} / {stats.totalPersons}</span>
                                        </div>
                                        <div className="rounded-pill overflow-hidden" style={{ height: 10, background: 'rgba(255,255,255,0.08)' }}>
                                            <div className="h-100 rounded-pill" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #198754, #75b798)', transition: 'width 0.8s ease' }} />
                                        </div>
                                    </div>
                                )
                            })()} */}

                            {/* Répartition masque / sans masque */}
                            {stats.totalImages > 0 && (() => {
                                const noMaskPct = (stats.breakdown.no_mask / stats.totalImages) * 100
                                const maskPct   = (stats.breakdown.mask   / stats.totalImages) * 100
                                return (
                                    <div>
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-white fw-semibold" style={{ fontSize: '0.85rem' }}>Répartition des classes</span>
                                            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>
                                                🙂 {noMaskPct.toFixed(1)} % &nbsp;·&nbsp; 😷 {maskPct.toFixed(1)} %
                                            </span>
                                        </div>
                                        <div className="d-flex rounded-pill overflow-hidden" style={{ height: 10, background: 'rgba(255,255,255,0.08)' }}>
                                            <div style={{ width: `${noMaskPct}%`, background: '#75b798', transition: 'width 0.8s ease' }} />
                                            <div style={{ width: `${maskPct}%`,   background: '#ea868f', transition: 'width 0.8s ease' }} />
                                        </div>
                                        <div className="d-flex gap-3 mt-2" style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>
                                            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#75b798', marginRight: 4 }} />Sans masque</span>
                                            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#ea868f', marginRight: 4 }} />Avec masque</span>
                                        </div>
                                    </div>
                                )
                            })()}
                        </div>
                    </div>
                </section>
            )}

            {/* How it works */}
            <section id="how-it-works" className="py-5 px-3" style={{ background: 'rgba(0,0,0,0.25)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="container" style={{ maxWidth: 900 }}>
                    <h2 className="text-white fw-bold text-center mb-2" style={{ fontSize: '1.5rem' }}>Comment fonctionne la collecte ?</h2>
                    <p className="text-center mb-5" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                        Le processus est entièrement automatisé et guidé étape par étape.
                    </p>

                    <div className="row g-4">
                        {[
                            {
                                step: '01',
                                icon: '📋',
                                title: 'Consentement & identification',
                                desc: 'Vous renseignez votre nom, adresse e-mail et numéro de contact, puis acceptez les conditions d\'utilisation du dataset.',
                            },
                            {
                                step: '02',
                                icon: '🙂',
                                title: 'Capture sans masque',
                                desc: 'La caméra filme votre visage. L\'IA valide automatiquement la netteté, l\'alignement et l\'originalité de chaque image.',
                            },
                            {
                                step: '03',
                                icon: '😷',
                                title: 'Capture avec masque',
                                desc: 'Après la première session, vous enfilez un masque facial et répétez l\'opération pour constituer la seconde classe.',
                            },
                            {
                                step: '04',
                                icon: '🔒',
                                title: 'Envoi sécurisé',
                                desc: 'Les images validées sont envoyées vers le serveur et stockées de façon anonymisée par un identifiant unique.',
                            },
                        ].map(({ step, icon, title, desc }) => (
                            <div key={step} className="col-sm-6 col-lg-3">
                                <div className="h-100 p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fw-bold" style={{ color: '#6ea8fe', fontSize: '0.75rem', letterSpacing: '0.08em' }}>{step}</span>
                                        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
                                    </div>
                                    <h3 className="text-white fw-semibold mb-0" style={{ fontSize: '0.95rem' }}>{title}</h3>
                                    <p className="mb-0" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.83rem', lineHeight: 1.6 }}>{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-5">
                        <Link to="/start" className="btn btn-primary px-5 py-2 fw-semibold" style={{ borderRadius: 10 }}>
                            Commencer maintenant →
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="text-center py-4 px-3" style={{ background: 'rgba(0,0,0,0.35)', borderTop: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}>
                Projet de soutenance &mdash; Master Data Science &bull; Les données collectées sont destinées exclusivement à un usage académique.
            </footer>
        </div>
    )
}
